import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
  Divider,
  Chip,
  TextField,
} from '@mui/material';
import { ArrowBack, Check } from '@mui/icons-material';
import { CategoryAttribute, ColorOption } from '../types/foundObject';
import {
  getListedStringOptions,
  getOtherSpecifyFieldLabel,
  getSelectOtherState,
  isOtherSelectOption,
} from '../utils/selectOtherOption';

interface MobileAttributeSelectorProps {
  open: boolean;
  onClose: () => void;
  attribute: CategoryAttribute;
  value: string;
  onValueChange: (value: string) => void;
  dependsOnValue?: string;
  title: string;
}

interface SlideStep {
  title: string;
  options: string[];
  level: number;
}

const MobileAttributeSelector: React.FC<MobileAttributeSelectorProps> = ({
  open,
  onClose,
  attribute,
  value,
  onValueChange,
  dependsOnValue,
  title,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState<SlideStep[]>([]);
  const [selectedPath, setSelectedPath] = useState<string[]>([]);
  const [otherSpecifyMode, setOtherSpecifyMode] = useState(false);
  const [otherSpecifyDraft, setOtherSpecifyDraft] = useState('');
  const stepTitle = title || attribute.label;

  useEffect(() => {
    if (!open) return;

    if (attribute.type === 'conditional_select' && typeof attribute.options === 'object' && !Array.isArray(attribute.options)) {
      const optionsRecord = attribute.options as Record<string, string[]>;

      if (dependsOnValue && optionsRecord[dependsOnValue]) {
        setSteps([{
          title: stepTitle,
          options: optionsRecord[dependsOnValue],
          level: 0,
        }]);
      } else {
        const allOptions = Object.values(optionsRecord).flat();
        const uniqueOptions = Array.from(new Set(allOptions));
        setSteps([{
          title: stepTitle,
          options: uniqueOptions,
          level: 0,
        }]);
      }
    } else if (attribute.type === 'select' && Array.isArray(attribute.options)) {
      setSteps([{
        title: stepTitle,
        options: attribute.options,
        level: 0,
      }]);
    } else if (attribute.type === 'color_select' && attribute.colorOptions) {
      setSteps([{
        title: stepTitle,
        options: attribute.colorOptions.map((option) => option.name),
        level: 0,
      }]);
    }

    setCurrentStep(0);
    setSelectedPath([]);
    setOtherSpecifyMode(false);
    setOtherSpecifyDraft('');
  }, [open, attribute, dependsOnValue, stepTitle]);

  const currentStepData = steps[currentStep];
  const currentListedOptions = currentStepData ? getListedStringOptions(currentStepData.options) : [];

  const handleOptionSelect = (option: string) => {
    if (isOtherSelectOption(option)) {
      const { otherSpecifyValue } = getSelectOtherState(value, currentListedOptions);
      setOtherSpecifyMode(true);
      setOtherSpecifyDraft(otherSpecifyValue);
      return;
    }

    onValueChange(option);
    onClose();
  };

  const handleOtherSpecifyConfirm = () => {
    const trimmed = otherSpecifyDraft.trim();
    onValueChange(trimmed || 'Autre');
    onClose();
  };

  const handleBack = () => {
    if (otherSpecifyMode) {
      setOtherSpecifyMode(false);
      setOtherSpecifyDraft('');
      return;
    }

    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setSelectedPath(selectedPath.slice(0, -1));
    } else {
      onClose();
    }
  };

  const getContrastColor = (hexColor: string | null): string => {
    if (!hexColor) {
      return '#000000';
    }

    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? '#000000' : '#ffffff';
  };

  const renderColorChip = (colorOption: ColorOption) => (
    <Chip
      key={colorOption.value}
      label={colorOption.name}
      style={{
        backgroundColor: colorOption.hex || '#f5f5f5',
        color: getContrastColor(colorOption.hex),
        margin: '4px',
        border: value === colorOption.name ? '2px solid #1976d2' : '1px solid #ccc',
      }}
      onClick={() => handleOptionSelect(colorOption.name)}
    />
  );

  if (!isMobile) return null;

  const { showOtherSpecify: valueIsOtherCustom } = getSelectOtherState(
    value,
    currentListedOptions.length > 0 ? currentListedOptions : getListedStringOptions(steps[0]?.options ?? [])
  );
  const otherSpecifyFieldLabel = getOtherSpecifyFieldLabel(attribute.label || title);

  const isOptionSelected = (optionValue: string) => {
    if (value === optionValue) {
      return true;
    }
    return isOtherSelectOption(optionValue) && (isOtherSelectOption(value) || valueIsOtherCustom);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton onClick={handleBack} edge="start">
          <ArrowBack />
        </IconButton>
        <Typography variant="h6" sx={{ flex: 1 }}>
          {otherSpecifyMode ? otherSpecifyFieldLabel : currentStepData?.title || title}
        </Typography>
        {otherSpecifyMode ? (
          <Button onClick={handleOtherSpecifyConfirm} size="small" disabled={!otherSpecifyDraft.trim()}>
            Valider
          </Button>
        ) : null}
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {otherSpecifyMode ? (
          <Box sx={{ p: 3 }}>
            <TextField
              fullWidth
              autoFocus
              label={otherSpecifyFieldLabel}
              value={otherSpecifyDraft}
              onChange={(e) => setOtherSpecifyDraft(e.target.value)}
            />
          </Box>
        ) : attribute.type === 'color_select' && attribute.colorOptions ? (
          <Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {attribute.colorOptions.map((colorOption) => renderColorChip(colorOption))}
          </Box>
        ) : (
          <List>
            {currentStepData?.options.map((option, index) => (
              <React.Fragment key={option}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleOptionSelect(option)}
                    selected={isOptionSelected(option)}
                  >
                    <ListItemText primary={option} />
                    {isOptionSelected(option) && <Check color="primary" />}
                  </ListItemButton>
                </ListItem>
                {index < (currentStepData?.options.length ?? 0) - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Annuler
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MobileAttributeSelector;
